<script setup>
import Icon from '@/components/shared/icons/Icon.vue'
import TextInput from '@/components/shared/form/TextInput.vue'
import CloseButton from '@/components/shared/buttons/CloseButton.vue'
import {useFocus} from '@/composables/use-focus.js'
import {toRefs, ref, computed, watch, watchEffect} from 'vue'

//Emits and props
//NOTE: It shouldn't be necessary to redefine these here for Vue 3
const emit = defineEmits([
  'search',
  'full-search',
  'partial-search',
  'clear-search',
  'input',
  'keydown',
  'focus',
  'blur',
])
const props = defineProps({
  name: {
    type: String,
    default: 'search',
  },
  searchTerm: {
    type: String,
    default: '',
  },
  debounce: {
    type: Number,
    default: 250,
  },
  autocomplete: {
    type: String,
    default: 'off',
  },
})

//External search term prop
const {searchTerm} = toRefs(props)

//Local search term refs
const localSearchTerm = ref('')
const searchEvent = computed(() => ({searchTerm: localSearchTerm.value}))

//Helper to set local search term
const setLocalSearchTerm = (value) => localSearchTerm.value = value
const clearLocalSearchTerm = () => setLocalSearchTerm('')

//Perform searches. A partial search event is triggered when a user is just typing,
//whereas a full search is triggered when a user presses enter. A regular search
//event is emitted in both cases in case the component consumer doesn't care how
//the search was triggered.
const clearSearch = () => {
  emit('clear-search')
}
const partialSearch = () => {
  emit('partial-search', searchEvent.value)
  emit('search', searchEvent.value)
}
const fullSearch = event => {

  //Give the keyboard item selection handler time to block the search
  //when Enter is pressed
  setTimeout(() => {
    if (!event.defaultPrevented) {
      event.target.blur()
      emit('full-search', searchEvent.value)
      emit('search', searchEvent.value)
    }
  }, 50)
}

//Update local search term ref
const onInput = (event) => {
  emit('input', event)
  if (!event.defaultPrevented) {
    setLocalSearchTerm(event.value)
  }
}

//Handle focus events to perform a partial search if needed
const onFocus = (event) => {
  emit('focus', event)
  if (!event.defaultPrevented) {
    if (localSearchTerm.value && localSearchTerm.value.length > 2) {
      partialSearch()
    }
  }
}

//Other handlers
const onBlur = (event) => emit('blur', event)
const onKeydown = (event) => emit('keydown', event)

//Watch the search term prop to clear the local value
//This allows the search to be cleared from a parent component
watchEffect(() => {
  if (searchTerm.value === '') {
    clearLocalSearchTerm()
  }
})

//Watch the local search term for value changes to trigger a search
watch(localSearchTerm, () => {
  if (localSearchTerm.value) {
    if (localSearchTerm.value.length > 2) {
      partialSearch()
    }
  }
  else {
    clearSearch()
  }
})

//Expose focus and blur methods to allow parent components to focus/blur the input
const input = ref(null)
const {focus, blur} = useFocus(input)
defineExpose({
  focus,
  blur,
})
</script>

<template>
  <TextInput
    ref="input"
    type="search"
    v-bind="$attrs"
    :name="name"
    :value="localSearchTerm"
    :debounce="debounce"
    :autocomplete="autocomplete"
    @input="onInput($event)"
    @keydown.enter="fullSearch($event)"
    @keydown="onKeydown($event)"
    @focus="onFocus($event)"
    @blur="onBlur($event)"
  >
    <template #iconLeft>
      <Icon
        icon="search"
        is-clickable
        @click="focus()"
      />
    </template>

    <template #iconRight>
      <CloseButton
        v-if="localSearchTerm"
        aria-label="Clear search"
        class="p-search-input-clear-icon"
        test-id="searchInputClear"
        @click="clearLocalSearchTerm()"
      />
    </template>
  </TextInput>
</template>
